import React from 'react';
import {
  Box,
  Container,
  Paper,
  Grid,
  Skeleton,
  Card,
  CardContent,
  Divider,
} from '@mui/material';
import PageContainer from '../pageContainer';

interface DetailSkeletonProps {
  hasImage?: boolean;
  hasSections?: number;
}

export function DetailSkeleton({ hasImage = false, hasSections = 2 }: DetailSkeletonProps) {
  const sectionsArray = Array.from({ length: hasSections }, (_, i) => i);

  return (
    <PageContainer>
      <Container maxWidth="xl">
        {/* Header Skeleton */}
        <Box sx={{ mb: 3 }}>
          <Skeleton variant="text" width={200} height={40} sx={{ mb: 1 }} />
          <Skeleton variant="text" width={300} height={24} />
        </Box>

        {/* Breadcrumb Skeleton */}
        <Box sx={{ display: 'flex', gap: 1, mb: 3 }}>
          <Skeleton variant="text" width={100} height={20} />
          <Skeleton variant="text" width={20} height={20} />
          <Skeleton variant="text" width={150} height={20} />
        </Box>

        {/* Back Button Skeleton */}
        <Skeleton variant="rectangular" width={180} height={36} sx={{ borderRadius: 1, mb: 3 }} />

        {/* Main Content Skeleton */}
        <Paper elevation={3} sx={{ p: 4, mb: 3, borderRadius: 2 }}>
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                <Skeleton variant="circular" width={24} height={24} />
                <Skeleton variant="text" width={200} height={28} />
              </Box>
              <Box>
                <Skeleton variant="text" width="100%" height={24} sx={{ mb: 1 }} />
                <Skeleton variant="text" width="95%" height={24} sx={{ mb: 1 }} />
                <Skeleton variant="text" width="90%" height={24} sx={{ mb: 1 }} />
                <Skeleton variant="text" width="85%" height={24} />
              </Box>
            </Grid>

            <Grid item xs={12} md={6}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                <Skeleton variant="circular" width={24} height={24} />
                <Skeleton variant="text" width={180} height={28} />
              </Box>
              <Box>
                <Box sx={{ mb: 2, display: 'flex', gap: 1 }}>
                  <Skeleton variant="rectangular" width={80} height={24} sx={{ borderRadius: 2 }} />
                  <Skeleton variant="rectangular" width={80} height={24} sx={{ borderRadius: 2 }} />
                </Box>
                <Skeleton variant="text" width="100%" height={20} sx={{ mb: 2 }} />
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  <Skeleton variant="rectangular" width="100%" height={48} sx={{ borderRadius: 1 }} />
                  <Skeleton variant="rectangular" width="100%" height={48} sx={{ borderRadius: 1 }} />
                </Box>
              </Box>
            </Grid>
          </Grid>

          <Divider sx={{ my: 3 }} />

          {/* Image Skeleton if needed */}
          {hasImage && (
            <Box sx={{ mb: 4 }}>
              <Skeleton
                variant="rectangular"
                width="100%"
                height={400}
                sx={{ borderRadius: 2 }}
              />
            </Box>
          )}

          {/* Content Skeleton */}
          <Box>
            <Skeleton variant="text" width="95%" height={24} sx={{ mb: 1 }} />
            <Skeleton variant="text" width="100%" height={24} sx={{ mb: 1 }} />
            <Skeleton variant="text" width="98%" height={24} sx={{ mb: 1 }} />
            <Skeleton variant="text" width="92%" height={24} sx={{ mb: 1 }} />
            <Skeleton variant="text" width="97%" height={24} sx={{ mb: 2 }} />
            <Skeleton variant="text" width="100%" height={24} sx={{ mb: 1 }} />
            <Skeleton variant="text" width="93%" height={24} sx={{ mb: 1 }} />
            <Skeleton variant="text" width="70%" height={24} />
          </Box>
        </Paper>

        {/* Additional Sections Skeleton */}
        {sectionsArray.map((index) => (
          <Card key={`section-${index}`} elevation={3} sx={{ mb: 3, borderRadius: 2 }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                <Skeleton variant="circular" width={24} height={24} />
                <Skeleton variant="text" width={250} height={28} />
              </Box>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <Skeleton variant="rectangular" width="100%" height={48} sx={{ borderRadius: 1 }} />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Skeleton variant="rectangular" width="100%" height={48} sx={{ borderRadius: 1 }} />
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        ))}
      </Container>
    </PageContainer>
  );
}
